import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { BookOpen, GraduationCap, PenTool, Backpack, Apple, Calculator, Globe, Palette } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { DebugOverlay } from '../../debug';
import { KioskCloseButton } from '../../features/klassenbuch-kiosk/components';

function decodeJwtPayload(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payloadBase64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const decoded = typeof window !== 'undefined' && window.atob ? window.atob(payloadBase64) : atob(payloadBase64);
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

async function readPersistedKioskToken() {
  const local = typeof window !== 'undefined' ? window.localStorage.getItem('fw_kiosk_jwt') : null;
  if (local) {
    // Migration path: older installs may only have localStorage token.
    // Best-effort sync to native storage enables cross-user persistence.
    try {
      const nativeToken = await window.kiosk?.getToken?.();
      const missingNative = !(nativeToken?.ok && nativeToken?.data?.access_token);
      if (missingNative) {
        const write = await window.kiosk?.setToken?.({ access_token: local });
        if (!write?.ok) {
          console.warn('Failed to sync local kiosk token to native storage:', write?.error || 'unknown error');
        }
      }
    } catch {
      // Ignore migration sync failures; local token still allows current user session.
    }
    return local;
  }
  try {
    const nativeToken = await window.kiosk?.getToken?.();
    if (nativeToken?.ok && nativeToken?.data?.access_token) {
      const token = nativeToken.data.access_token;
      window.localStorage.setItem('fw_kiosk_jwt', token);
      return token;
    }
  } catch {
    // Ignore native bridge failures and continue with normal setup flow.
  }
  return null;
}

async function persistKioskToken(token) {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem('fw_kiosk_jwt', token);
  }
  try {
    await window.kiosk?.setToken?.({ access_token: token });
    await window.kiosk?.migrateTokenNow?.();
  } catch {
    // LocalStorage is still the runtime source; native write is best-effort.
  }
}

/**
 * Persist the long-lived device key returned by kiosk-login. The Electron
 * shell writes it to device.key, which a logoDIDACT admin bakes into the
 * base image — so after a reboot wipes the JWT the kiosk re-authenticates
 * via kiosk-device-login instead of dropping back to this pairing screen.
 * No-op outside the Electron shell (browser preview, non-logoDIDACT installs
 * keep using the JWT persistence path).
 */
async function persistDeviceKey(deviceKey) {
  if (!deviceKey || !window.kiosk?.setDeviceKey) return;
  try {
    const res = await window.kiosk.setDeviceKey(deviceKey);
    if (!res?.ok) {
      console.warn('Failed to persist kiosk device key:', res?.error || 'unknown error');
    }
  } catch (e) {
    console.warn('Unexpected error persisting kiosk device key:', e);
  }
}

/** Send the kiosk to the view its JWT allows. Shared by every login path. */
function redirectForAllowedViews(allowedViews) {
  const views = Array.isArray(allowedViews) ? allowedViews : [];
  if (views.includes('klassenbuch')) {
    window.location.replace('/kiosk/klassenbuch');
  } else if (views.includes('flex_overview')) {
    window.location.replace('/kiosk/display/flexband');
  } else {
    window.location.replace('/kiosk/display/substitution');
  }
}

/**
 * Boot-time device-key auto-login. Runs when no usable JWT is present —
 * the normal state on a logoDIDACT machine after a reboot wipes the
 * overlay. If a device.key survives in the base image, exchange it for a
 * fresh JWT via the kiosk-device-login edge function and skip the pairing
 * screen entirely.
 *
 * Returns true if it redirected (caller must stop), false to fall through
 * to normal pairing.
 */
async function tryDeviceKeyLogin() {
  if (!window.kiosk?.getDeviceKey) return false; // not in the Electron shell
  let deviceKey = null;
  try {
    const stored = await window.kiosk.getDeviceKey();
    deviceKey = stored?.ok ? stored.key : null;
  } catch (e) {
    console.warn('Error reading device key:', e);
    return false;
  }
  if (!deviceKey) return false;

  try {
    const { data, error: fnError } = await supabase.functions.invoke('kiosk-device-login', {
      body: { device_key: deviceKey }
    });

    if (fnError || !data || data.status !== 'ok' || !data.access_token) {
      // A revoked or unknown key will never work again — drop it so the
      // kiosk doesn't retry the dead key on every boot.
      if (data?.code === 'device_revoked' || data?.code === 'device_not_found') {
        try { await window.kiosk?.clearDeviceKey?.(); } catch { /* best-effort */ }
      }
      console.warn('Device-key login did not succeed:', fnError || data?.code || data);
      return false;
    }

    await persistKioskToken(data.access_token);
    // kiosk-device-login returns allowed_views directly — use it rather
    // than decoding the JWT, which would add an avoidable failure mode.
    redirectForAllowedViews(data.allowed_views);
    return true;
  } catch (e) {
    console.error('Unexpected error during device-key login:', e);
    return false;
  }
}

async function clearPersistedKioskToken() {
  try {
    window.localStorage.removeItem('fw_kiosk_jwt');
  } catch {
    // Ignore local storage failures.
  }
  try {
    await window.kiosk?.clearToken?.();
  } catch {
    // Ignore native bridge failures.
  }
}

// KioskSetupPage: monitor-side pairing screen at /kiosk/setup.
export default function KioskSetupPage() {
  const [pairingCode, setPairingCode] = useState('');
  const [expiresAt, setExpiresAt] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [status, setStatus] = useState('idle'); // 'idle' | 'pending' | 'completed' | 'expired' | 'error'
  const [statusMessage, setStatusMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [refreshNotice, setRefreshNotice] = useState('');
  const [hasLoggedIn, setHasLoggedIn] = useState(false);
  const [loginError, setLoginError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      // Persistence Check: redirect if a valid token already exists (local or native).
      const storedToken = await readPersistedKioskToken();
      if (storedToken) {
        try {
          const payload = decodeJwtPayload(storedToken);
          const now = Date.now() / 1000;
          if (payload && payload.exp && payload.exp > now) {
            if (payload.allowed_views && payload.allowed_views.includes('klassenbuch')) {
              window.location.replace('/kiosk/klassenbuch');
            } else if (payload.allowed_views && payload.allowed_views.includes('flex_overview')) {
              window.location.replace('/kiosk/display/flexband');
            } else {
              window.location.replace('/kiosk/display/substitution');
            }
            return;
          }
          await clearPersistedKioskToken();
        } catch (e) {
          console.error('Error validating stored token:', e);
          await clearPersistedKioskToken();
        }
      }

      // No usable JWT. Before showing the pairing screen, try the device
      // key — on a logoDIDACT machine the JWT is wiped every reboot but
      // device.key survives in the base image, so this is the normal path.
      if (cancelled) return;
      const deviceKeyLoggedIn = await tryDeviceKeyLogin();
      if (deviceKeyLoggedIn || cancelled) return;

      await createPairing({ cancelledRef: () => cancelled, reason: 'initial' });
    };

    void bootstrap();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!pairingCode) return;

    let cancelled = false;
    let intervalId;

    const pollStatus = async () => {
      if (cancelled || !pairingCode) return;

      try {
        const { data, error: rpcError } = await supabase.rpc('fn_monitor_get_pairing_status', {
          p_code: pairingCode
        });

        if (cancelled) return;

        if (rpcError) {
          // Keep showing current code, but surface a minimal error.
          console.error('Error polling monitor pairing status:', rpcError);
          setError('Fehler beim Abfragen des Pairing-Status. Versuche es in Kürze erneut.');
          return;
        }

        const statusValue = data && data.status ? data.status : 'error';

        if (statusValue === 'pending') {
          setStatus('pending');
          setStatusMessage('Warte auf Bestätigung auf dem Handy…');
          setError('');
        } else if (statusValue === 'completed') {
          setStatus('completed');
          setStatusMessage('Monitor erfolgreich verbunden. Anmeldung wird durchgeführt…');
          setError('');
          if (intervalId) {
            clearInterval(intervalId);
          }
          if (!hasLoggedIn && pairingCode) {
            setHasLoggedIn(true);
            await performKioskLogin(pairingCode, () => cancelled);
          }
        } else if (statusValue === 'expired') {
          setStatus('expired');
          setStatusMessage('Code abgelaufen, neuer Code wird erstellt…');
          setError('');
          if (intervalId) {
            clearInterval(intervalId);
          }
          await createPairing({ cancelledRef: () => cancelled, reason: 'expired' });
        } else {
          setStatus('error');
          setStatusMessage('');
          setError('Ungültige Antwort vom Server für diesen Pairing-Code.');
        }
      } catch (e) {
        if (cancelled) return;
        console.error('Unexpected error while polling pairing status:', e);
        setError('Unerwarteter Fehler beim Abfragen des Pairing-Status.');
      }
    };

    pollStatus();
    intervalId = window.setInterval(pollStatus, 3000);

    return () => {
      cancelled = true;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [pairingCode]);

  // Local expiry timer so the monitor refreshes even if polling is throttled.
  useEffect(() => {
    if (!expiresAt) return;
    if (status === 'completed') return;

    let cancelled = false;
    const expiresMs = new Date(expiresAt).getTime() - Date.now();
    const delay = Math.max(expiresMs + 2000, 0);

    const timeoutId = window.setTimeout(() => {
      if (cancelled) return;
      createPairing({ cancelledRef: () => cancelled, reason: 'expired-timer' });
    }, delay);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [expiresAt, status]);

  const createPairing = async ({ cancelledRef, reason }) => {
    if (cancelledRef && cancelledRef()) return;

    try {
      setLoading(true);
      setError('');
      setLoginError('');
      if (reason === 'expired') {
        setRefreshNotice('Code abgelaufen, neuer Code wurde erstellt.');
        // Auto-clear notice after 5 seconds
        setTimeout(() => setRefreshNotice(''), 5000);
      } else {
        setRefreshNotice('');
      }

      const { data, error: rpcError } = await supabase.rpc('fn_monitor_create_pairing');

      if (cancelledRef && cancelledRef()) return;

      if (rpcError) {
        console.error('Error creating monitor pairing:', rpcError);
        setStatus('error');
        setStatusMessage('');
        setError('Fehler beim Erzeugen des Pairing-Codes. Bitte erneut versuchen.');
        return;
      }

      if (!data || !data.pairing_code) {
        setStatus('error');
        setStatusMessage('');
        setError('Unerwartete Antwort beim Erzeugen des Pairing-Codes.');
        return;
      }

      const newCode = String(data.pairing_code).toUpperCase();
      const expires = data.expires_at || null;

      setPairingCode(newCode);
      setExpiresAt(expires);
      setStatus('pending');
      setStatusMessage('Warte auf Bestätigung auf dem Handy…');
      setHasLoggedIn(false);
      setLoginError('');

      const baseUrl = 'https://flexwise.io'; // Always use production URL for pairing
      const pairingUrl = `${baseUrl}/kiosk/pair?code=${encodeURIComponent(newCode)}`;
      await generateQrCode(pairingUrl, cancelledRef);
    } catch (e) {
      if (cancelledRef && cancelledRef()) return;
      console.error('Unexpected error creating monitor pairing:', e);
      setStatus('error');
      setStatusMessage('');
      setError('Unerwarteter Fehler beim Erzeugen des Pairing-Codes.');
    } finally {
      if (!cancelledRef || !cancelledRef()) {
        setLoading(false);
      }
    }
  };

  const performKioskLogin = async (code, cancelledRef) => {
    if (!code) return;
    try {
      setLoading(true);
      setError('');
      setLoginError('');

      const { data, error: fnError } = await supabase.functions.invoke('kiosk-login', {
        body: { pairing_code: code }
      });

      if (cancelledRef && cancelledRef()) return;

      if (fnError || !data || data.status !== 'ok' || !data.access_token) {
        console.error('Error during kiosk-login edge function:', fnError || data);
        setStatus('error');
        setStatusMessage('');
        setLoginError('Login fehlgeschlagen. Bitte versuchen Sie es erneut.');

        setTimeout(() => {
          setLoginError('');
          createPairing({ cancelledRef: () => false, reason: 'login-error-retry' });
        }, 5000);
        return;
      }

      try {
        await persistKioskToken(data.access_token);
        await persistDeviceKey(data.device_key);
      } catch (sessionError) {
        console.error('Error storing kiosk JWT:', sessionError);
        setStatus('error');
        setStatusMessage('');
        setLoginError('Login fehlgeschlagen. Bitte versuchen Sie es erneut.');

        setTimeout(() => {
          setLoginError('');
          createPairing({ cancelledRef: () => false, reason: 'login-error-retry' });
        }, 5000);
        return;
      }

      if (typeof window !== 'undefined') {
        try {
          // Decode token to determine redirect target
          const payload = decodeJwtPayload(data.access_token);
          const allowedViews = payload && Array.isArray(payload.allowed_views) ? payload.allowed_views : [];

          if (allowedViews.includes('klassenbuch')) {
            window.location.replace('/kiosk/klassenbuch');
          } else if (allowedViews.includes('flex_overview')) {
            window.location.replace('/kiosk/display/flexband');
          } else {
            window.location.replace('/kiosk/display/substitution');
          }
        } catch (redirectError) {
          console.error('Error redirecting monitor after successful kiosk login:', redirectError);
        }
      }
    } catch (e) {
      console.error('Unexpected error during kiosk-login:', e);
      setStatus('error');
      setStatusMessage('');
      setLoginError('Login fehlgeschlagen. Bitte versuchen Sie es erneut.');

      setTimeout(() => {
        setLoginError('');
        createPairing({ cancelledRef: () => false, reason: 'login-error-retry' });
      }, 5000);
    } finally {
      setLoading(false);
    }
  };

  const generateQrCode = async (url, cancelledRef) => {
    try {
      const dataUrl = await QRCode.toDataURL(url, {
        errorCorrectionLevel: 'M',
        margin: 1,
        width: 400
      });
      if (cancelledRef && cancelledRef()) return;
      setQrDataUrl(dataUrl);
    } catch (e) {
      if (cancelledRef && cancelledRef()) return;
      console.error('Error generating kiosk pairing QR code:', e);
      setQrDataUrl('');
    }
  };

  const handleRetry = () => {
    createPairing({ cancelledRef: () => false, reason: 'manual' });
  };

  const renderStatusText = () => {
    if (error) {
      return (
        <p className="mt-3 text-sm text-red-400">
          {error}
        </p>
      );
    }

    if (statusMessage) {
      return (
        <p className="mt-3 text-sm text-slate-200">
          {statusMessage}
        </p>
      );
    }

    return null;
  };

  const renderExpiresAt = () => {
    if (!expiresAt) return null;
    try {
      const date = new Date(expiresAt);
      const formatted = date.toLocaleTimeString('de-DE', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      return (
        <p className="mt-2 text-xs text-slate-400">
          Gültig bis ca. {formatted}
        </p>
      );
    } catch {
      return null;
    }
  };

  return (
    <DebugOverlay name="KioskSetupPage">
      <KioskCloseButton />
      <div className="min-h-screen flex flex-col items-center justify-center bg-white text-slate-900 px-4 py-8 relative overflow-hidden">
        {/* Background Icons matching Login Theme */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Top left area */}
          <BookOpen className="absolute top-16 left-16 w-16 h-16 text-slate-200 rotate-12" />
          <PenTool className="absolute top-32 left-32 w-12 h-12 text-slate-300 -rotate-6" />
          <Calculator className="absolute top-24 left-64 w-14 h-14 text-slate-200 rotate-45" />

          {/* Top right area */}
          <GraduationCap className="absolute top-20 right-20 w-20 h-20 text-slate-200 -rotate-12" />
          <Globe className="absolute top-40 right-32 w-16 h-16 text-slate-300 rotate-30" />
          <Apple className="absolute top-60 right-16 w-12 h-12 text-slate-300 rotate-15" />

          {/* Bottom left area */}
          <Backpack className="absolute bottom-32 left-20 w-18 h-18 text-slate-200 rotate-6" />
          <Palette className="absolute bottom-16 left-40 w-14 h-14 text-slate-300 -rotate-20" />

          {/* Bottom right area */}
          <BookOpen className="absolute bottom-28 right-28 w-16 h-16 text-slate-200 -rotate-30" />
          <PenTool className="absolute bottom-12 right-12 w-12 h-12 text-slate-300 rotate-45" />
        </div>

        <div className="max-w-xl w-full text-center space-y-8 z-10 relative">
          <div>
            <div className="flex justify-center mb-6">
              <img src="/flexwise-logo-with-tagline.png" alt="FlexWise" className="h-24 w-auto drop-shadow-lg" />
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">Monitor-Einrichtung</h1>
            <p className="mt-2 text-md text-slate-500">
              Scannen Sie diesen QR-Code mit einem angemeldeten Admin auf dem Handy,
              um diesen Monitor mit FlexWise zu verbinden.
            </p>
          </div>

          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl border border-slate-200 shadow-xl">
              <p className="text-xs uppercase tracking-[0.25em] text-slate-500 font-semibold mb-2">Pairing-Code</p>
              <p className="font-mono text-6xl tracking-[0.25em] text-slate-900 font-bold">
                {pairingCode || '••••••'}
              </p>
              {renderExpiresAt()}
            </div>

            <div className="mt-8 bg-white p-4 rounded-2xl shadow-xl border border-slate-100 inline-block transform hover:scale-105 transition-transform duration-300">
              {qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  alt="QR-Code für Monitor-Pairing"
                  className="block w-64 h-64 object-contain"
                />
              ) : (
                <div className="w-64 h-64 flex items-center justify-center text-sm text-slate-400">
                  <div className="animate-pulse">QR-Code wird geladen…</div>
                </div>
              )}
            </div>
          </div>

          {refreshNotice && (
            <div className="bg-amber-50 text-amber-800 px-4 py-2 rounded-lg border border-amber-200 text-sm inline-block">
              {refreshNotice}
            </div>
          )}

          {renderStatusText()}

          {status === 'error' && (
            <div className="mt-4">
              <button
                type="button"
                onClick={handleRetry}
                disabled={loading}
                className="inline-flex items-center justify-center rounded-full bg-slate-900 px-6 py-3 text-sm font-medium text-white shadow-lg hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Code wird erstellt…' : 'Neuen Code anfordern'}
              </button>
            </div>
          )}
        </div>

        {loginError && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/80 backdrop-blur-sm px-4">
            <div className="max-w-md w-full text-center space-y-4 bg-white p-8 rounded-2xl shadow-2xl">
              <h2 className="text-2xl font-bold text-slate-900">Login fehlgeschlagen</h2>
              <p className="text-slate-600">{loginError}</p>
              <p className="text-sm text-slate-400">
                Ein neuer Pairing-Code wird in Kürze erstellt…
              </p>
            </div>
          </div>
        )}
      </div>
    </DebugOverlay>
  );
}
